import React from 'react';
import { Button, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import AdminSearchField from '../../../../components/admin/AdminSearchField';
import StoreUsersTable from '../../../../components/storeDetails/StoreUsersTable';
import type { StoreUser } from '../../../../services/authService';
import { adminButtonSx, adminButtonToneSx, adminCombineSx, adminFormControlSx, adminFormFieldSx } from '../../../../styles/adminComponentStyles';

type StoreDetailsUsersTabProps = {
    users: StoreUser[];
    loading: boolean;
    error: string;
    mutatingUserId?: string | null;
    removingUserId?: string | null;
    ownerUserId?: string | null;
    onRemoveUser?: (user: StoreUser) => void;
    onToggleDailyReconciliationEmail?: (user: StoreUser) => void;
    onToggleVisibility?: (user: StoreUser) => void;
    onAddUser: () => void;
};

const StoreDetailsUsersTab: React.FC<StoreDetailsUsersTabProps> = ({
    users,
    loading,
    error,
        mutatingUserId,
        removingUserId,
    ownerUserId,
    onRemoveUser,
    onToggleDailyReconciliationEmail,
    onToggleVisibility,
    onAddUser,
}) => (
    <StoreDetailsUsersTabContent
        users={users}
        loading={loading}
        error={error}
        mutatingUserId={mutatingUserId}
        removingUserId={removingUserId}
        ownerUserId={ownerUserId}
        onRemoveUser={onRemoveUser}
        onToggleDailyReconciliationEmail={onToggleDailyReconciliationEmail}
        onToggleVisibility={onToggleVisibility}
        onAddUser={onAddUser}
    />
);

const StoreDetailsUsersTabContent: React.FC<StoreDetailsUsersTabProps> = ({
    users,
    loading,
    error,
    mutatingUserId,
    removingUserId,
    ownerUserId,
    onRemoveUser,
    onToggleDailyReconciliationEmail,
    onToggleVisibility,
    onAddUser,
}) => {
    const [search, setSearch] = React.useState('');
    const [role, setRole] = React.useState('all');

    const roles = React.useMemo(
        () => Array.from(new Set(users.map((user) => user.role).filter(Boolean))).sort(),
        [users],
    );
    const filteredUsers = React.useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        return users.filter((user) => {
            const matchesRole = role === 'all' || user.role === role;
            const searchableText = [user.display_name, user.contact_email, user.phone_number, user.role]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return matchesRole && (!normalizedSearch || searchableText.includes(normalizedSearch));
        });
    }, [role, search, users]);

    return (
        <>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 1.5, px: 2 }}
            >
                <AdminSearchField
                    placeholder="Search..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    sx={adminCombineSx(adminFormFieldSx, { width: { xs: '100%', sm: 220 } })}
                    inputProps={{ 'aria-label': 'Search users' }}
                />
                <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ minWidth: 0 }}>
                    <FormControl size="small" sx={adminCombineSx(adminFormControlSx, { minWidth: { sm: 160 } })}>
                        <InputLabel>Role</InputLabel>
                        <Select
                            label="Role"
                            value={role}
                            onChange={(event) => setRole(event.target.value)}
                        >
                            <MenuItem value="all">All Roles</MenuItem>
                            {roles.map((userRole) => (
                                <MenuItem key={userRole} value={userRole}>
                                    {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={onAddUser}
                        sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}
                    >
                        Add User
                    </Button>
                </Stack>
            </Stack>
            <StoreUsersTable
                users={filteredUsers}
                loading={loading}
                error={error}
                mutatingUserId={mutatingUserId}
                removingUserId={removingUserId}
                ownerUserId={ownerUserId}
                onRemoveUser={onRemoveUser}
                onToggleDailyReconciliationEmail={onToggleDailyReconciliationEmail}
                onToggleVisibility={onToggleVisibility}
            />
        </>
    );
};

export default React.memo(StoreDetailsUsersTab);
